import { ColDef, GridApi, GridOptions, Module } from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import { RootState } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import {
  ExternalPositionDetail,
  properties,
} from '../model/external-position-detail.model'
import { Position } from '../model/positions.model'
import { PositionsService } from '../services/positions.service'

const externalPositionDetailPath = composeFieldPath(
  [] as ExternalPositionDetail[],
)

@Component({
  selector: 'hs-external-position-detail',
  templateUrl: './external-position-detail-panel.component.html',
  styleUrls: ['./external-position-detail-panel.component.scss'],
})
export class ExternalPositionDetailPanelComponent implements OnDestroy {
  isCwOnly = false
  _position?: Position
  @Input() isMultipleSelected = false
  @Input() set position(value: Position | undefined) {
    if (this._position != value) {
      this._position = value
      this.isCwOnly = this._position?.matchStatus == 'CW_ONLY'
      this.reloadData()
    }
  }

  data: ExternalPositionDetail[] = []

  private readonly destroySubject$ = new Subject<void>()

  isLoading = false

  readonly gridModules: Module[] = [FiltersToolPanelModule]

  /** @internal */
  gridApi?: GridApi
  private readonly columnDefs = this.createColumnDefs()

  /** @internal */
  readonly gridOptions: GridOptions = this.createGridOptions()

  constructor(
    private readonly positionsService: PositionsService,
    private readonly store: Store<RootState>,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  //#region GridConfig
  private createGridOptions(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      onGridReady: ({ api }) => {
        this.gridApi = api
        enableHeliosCopyBehavior(api)
      },

      columnDefs: this.columnDefs,
    }
  }

  /** @internal  */
  createColumnDefs(): ColDef[] {
    return [
      {
        field: externalPositionDetailPath('property'),
        flex: 1,
      },
      {
        field: externalPositionDetailPath('value'),
        tooltipValueGetter: ({ data }) => {
          if (data.property === 'Aggregate Information') {
            return JSON.stringify(data.value)
              .replace(/,/g, '\n')
              .replace(/"/g, '')
              .replace(/\\/g, '')
              .replace(/:/g, ': ')
          } else {
            return data.value
          }
        },
        flex: 2,
      },
    ]
  }

  camelCaseToNormal(input: string): string {
    const text = String(input)

    let result = text.replace(/([a-z])([A-Z])/g, '$1 $2')
    result = result.charAt(0).toUpperCase() + result.slice(1)

    return result
  }

  reloadData() {
    if (!this._position?.extPosId) return
    this.isLoading = true
    this.positionsService
      .getExternalPositionDetail(this._position?.extPosId)
      .subscribe(response => {
        const rowData: { property: string; value: string }[] = []

        properties.forEach(p => {
          rowData.push({ property: this.camelCaseToNormal(p), value: '' })
        })

        Object.entries(response).forEach(([key, val]) => {
          if (key == 'dataSource') {
            val = val.replace(/"/g, '')
          } else if (key == 'aggregateInformation') {
            val = val.replace(/"/g, '').replace(/,/g, ', ')
          }
          for (const row of rowData) {
            if (row.property === this.camelCaseToNormal(key)) {
              row.value = val
            }
          }
        })

        this.data = rowData
        this.isLoading = false
      })
  }
}
